import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/iconify';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useBoolean } from 'src/hooks/use-boolean';
import CalendarEventEditor from 'src/sections/calendar/modal/calendar-event-editor';
import { CalendarEventAvailability, CalendarEventVisibility, ICalendarEvent } from 'src/services/calendar/calendar.types';
import { getEventStatusColor, getEventTypeColor } from 'src/services/calendar/calendar.utils';
import { rootApi } from 'src/services/rootApi';
import { openCalendarEventViewer } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

// ----------------------------------------------------------------------

interface CalendarEventCardProps {
  event: ICalendarEvent;
}

export default function CalendarEventCard({ event }: CalendarEventCardProps) {

  const { t } = useTranslation();

  const { account } = useAuthContext();

  const theme = useTheme();

  const eventEditorControl = useBoolean();

  const dispatch = useAppDispatch();

  const [expanded, setExpanded] = useState(false);

  const getEventTypeLabel = (type: string): string => t(`calendar.event_types.${type.toLowerCase()}`);

  const getEventStatusLabel = (status: string): string => t(`calendar.event_status.${status.toLowerCase()}`);

  const formatEventTime = (): string => {
    if (event.all_day) {
      return t('calendar.all_day');
    }

    const startTime = moment(event.start_at).tz(account.timezone).format('h:mm a');

    if (event.end_at) {
      const endTime = moment(event.end_at).tz(account.timezone).format('h:mm a');
      return `${startTime} - ${endTime} (${account.timezone})`;
    }

    return startTime;
  };

  const formatEventDate = (): string => moment(event.start_at).tz(account.timezone).format('Do of MMMM, yyyy');

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    setExpanded(!expanded);
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!event) return;

    dispatch(openCalendarEventViewer({ eventId: event.id }));
  }

  const handleEditEvent = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!event) return;

    eventEditorControl.onTrue();
  }

  const handleOnEditSuccess = () => {
    eventEditorControl.onFalse();

    dispatch(rootApi.util.invalidateTags([{ type: 'CalendarEvents', id: event.calendar.id }]));
  };

  return (
    <>

      <Card
        onClick={handleExpandClick}
        sx={{
          cursor: 'pointer',
          borderLeft: `4px solid ${getEventTypeColor(event.type, theme)}`,
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: theme.shadows[4]
          },
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {/* Date indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: -8,
            bgcolor: theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: theme.shadows[2],
            p: 0.75,
            border: `1px solid ${theme.palette.divider}`,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 'bold',
              display: 'block',
              textAlign: 'center',
              color: getEventTypeColor(event.type, theme),
            }}
          >
            {format(new Date(event.start_at), 'MMM')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {format(new Date(event.start_at), 'd')}
          </Typography>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
          {/* header with chips and expand button */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            pr: { sm: 2 },
          }}>
            <Box>

              {/* title and date (mobile only) */}
              <Stack direction="row" alignItems="center" spacing={0} sx={{ mb: 0.5 }}>
                <Box >
                  {
                    event.availability_type === CalendarEventAvailability.BUSY ? (
                      <Tooltip title={t('calendar.availability_type_tooltips.busy')}>
                        <Iconify icon="icon-park-outline:dot" width={25} height={2250} color={theme.palette.error.main} />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('calendar.availability_type_tooltips.free')}>
                        <Iconify icon="icon-park-outline:dot" width={25} height={25} color={theme.palette.success.main} />
                      </Tooltip>
                    )
                  }

                </Box>

                <TruncatedTextField
                  onClick={handleViewDetails}
                  variant="h6"
                  mode='characters'
                  limit={100}
                  text={event.title}
                  sx={{
                    mb: 0.7,
                    lineHeight: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                />

              </Stack>
            </Box>
            <Stack direction="row" alignItems="center">
              <IconButton
                size="small"
                onClick={handleEditEvent}
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                <Iconify icon="mdi:pencil" />
              </IconButton>

              <IconButton
                size="small"
                onClick={handleViewDetails}
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  ml: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                <Iconify icon="mdi:eye" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  ml: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                <Iconify icon="mdi:chevron-down" />
              </IconButton>
            </Stack>
          </Box>


          {/* Event details in a grid layout */}
          <Stack direction="column" sx={{ alignItems: 'start', mb: 1 }}>

            {/* Time */}
            <Typography variant="body2" sx={{
              display: 'flex',
              alignItems: 'center',
              color: theme.palette.text.secondary
            }}>
              <Iconify icon="mdi:clock-outline" sx={{ mr: 1, width: 16, height: 16 }} />
              {formatEventDate()} {formatEventTime()}
            </Typography>

            {/* Location */}
            {event.location && (
              <Typography variant="body2" sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.text.secondary
              }}>
                <Iconify icon="mdi:map-marker" sx={{ mr: 1, width: 16, height: 16 }} />
                {event.location}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
            <Chip
              label={getEventTypeLabel(event.type)}
              size="small"
              sx={{
                bgcolor: alpha(getEventTypeColor(event.type, theme), 0.1),
                color: getEventTypeColor(event.type, theme),
                fontWeight: 'bold',
                height: 24,
              }}
            />
          </Stack>

          {/* Expanded content */}
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1.5 }}>
              <Divider sx={{ my: 1.5 }} />

              {event.description && (
                <Box sx={{ mb: 2, p: 1, bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {event.description}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {
        event && (
          <CalendarEventEditor
            open={eventEditorControl.value}
            onClose={eventEditorControl.onFalse}
            eventId={event.id}
            calendarId={event.calendar.id}
            onSuccess={handleOnEditSuccess}
          />
        )
      }
    </>
  );
}